import { memo, useCallback, useEffect, useState } from "react";
import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import { MessageResponse } from "../../../model/MessageResponse";
import { useDisclosure } from "@mantine/hooks";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { getProvinceSelectNew } from "../../../service/getSelectApi";
import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Fieldset,
  Flex,
  Grid,
  NumberInput,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import cx from "clsx";
import classes from "../../../Styles/TableScrollArea.module.css";
import { getValueById } from "../../../_base/helper/FunctionHelper";

// Khối lượng thực tế tối thiểu (kg); khối lượng đóng gói tối thiểu = 0.1 × 130%
const MIN_WEIGHT = 0.1;
const MIN_WEIGHT_PACKAGE = 0.13;

interface Products {
  quantity: number;
  weight: number;
  weightPackage: number;
  product_code: string;
  name: string;
}

interface ProductRowProps {
  item: Products;
  onUpdateWeight: (
    id: string,
    field: "weight" | "weightPackage",
    value: number,
  ) => void;
  isCOD?: boolean;
}

const ProductRow = memo(({ item, onUpdateWeight, isCOD }: ProductRowProps) => {
  return (
    <Table.Tr>
      <Table.Td w={100}>{item.product_code}</Table.Td>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td w={100}>{item.quantity}</Table.Td>
      <Table.Td w={isCOD ? 700 : 300}>
        <Flex align="center" gap={10}>
          <Text size="11.5px">Khối lượng thực tế</Text>
          <NumberInput
            placeholder="Khối lượng thực tế"
            value={item.weight}
            // Gọi hàm cập nhật cho weight
            onChange={(val) =>
              onUpdateWeight(item.product_code, "weight", Number(val))
            }
            hideControls
            allowNegative={false}
            // Nhập nhỏ hơn 0.1 thì khi rời ô sẽ tự đẩy lên 0.1
            min={MIN_WEIGHT}
            error={
              item.weight < MIN_WEIGHT ? `Tối thiểu ${MIN_WEIGHT} kg` : undefined
            }
            suffix=" kg"
            size="xs"
          />
          {isCOD && (
            <>
              <Text size="11.5px">× 130% = Khối lượng chênh lệch đóng gói</Text>
              <NumberInput
                placeholder="Khối lượng đóng gói"
                value={item.weightPackage}
                // Gọi hàm cập nhật cho weightPackage
                onChange={(val) =>
                  onUpdateWeight(
                    item.product_code,
                    "weightPackage",
                    Number(val),
                  )
                }
                hideControls
                allowNegative={false}
                min={MIN_WEIGHT_PACKAGE}
                error={
                  item.weightPackage < MIN_WEIGHT_PACKAGE
                    ? `Tối thiểu ${MIN_WEIGHT_PACKAGE} kg`
                    : undefined
                }
                suffix=" kg"
                size="xs"
              />
            </>
          )}
        </Flex>
      </Table.Td>
    </Table.Tr>
  );
});

const ConfirmInfoGHTKRequestTransfer = ({
  id,
  dataBranch,
  onSuccess,
  isCOD,
}: {
  id: number;
  dataBranch: any[];
  onSuccess: any;
  isCOD?: boolean;
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const [dataSubmit, setDataSubmit] = useState<any>({});
  const [totalBox, setTotalBox] = useState<number>(1);
  const [dataProductUpdateWeight, setDataProductUpdateWeight] = useState<
    Products[]
  >([]);
  const [dataEmployeeManagerItem, setDataEmployeeManagerItem] = useState<any[]>(
    [],
  );
  const [dataProvinceNew, setDataProvinceNew] = useState<ComboboxItem[]>([]);
  const [dataCommuneFromBranch, setDataCommuneFromBranch] = useState<
    ComboboxItem[]
  >([]);
  const [dataCommuneToBranch, setDataCommuneToBranch] = useState<
    ComboboxItem[]
  >([]);
  const [idDelivery, setIdDelivery] = useState<number>(0);

  const customHeightTable = () => {
    if (dataProductUpdateWeight.length <= 1 || !dataProductUpdateWeight) {
      return 110;
    } else if (dataProductUpdateWeight.length === 2) {
      return 150;
    } else if (dataProductUpdateWeight.length === 3) {
      return 190;
    } else if (dataProductUpdateWeight.length === 4) {
      return 230;
    } else {
      return 270;
    }
  };

  const handleUpdateWeight = useCallback(
    (id: string, field: "weight" | "weightPackage", value: number) => {
      setDataProductUpdateWeight((prevData) =>
        prevData.map((item) => {
          if (item.product_code === id) {
            const numValue = Number(value) || 0;

            if (field === "weight") {
              return {
                ...item,
                weight: numValue,
                weightPackage: Number(((numValue * 130) / 100).toFixed(2)),
              };
            }

            if (field === "weightPackage") {
              return {
                ...item,
                weightPackage: numValue,
                weight: Number(((numValue * 100) / 130).toFixed(2)),
              };
            }

            return { ...item, [field]: value };
          }
          return item;
        }),
      );
    },
    [],
  );

  const getDataProductWeight = async (dataProducts: any[]) => {
    let url = "/api/v1/TblItem/get-attribute54-by-item-codes";

    if (dataProducts && dataProducts.length > 0) {
      url += "?" + dataProducts.map((i) => `ItemCodes=${i.itemCode}`).join("&");
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data = res.data;

      const products = dataProducts.map((item) => ({
        quantity: Number(item.primaryQuantity),
        // API trả khối lượng < 0.1kg (hoặc không có) thì đẩy lên mức tối thiểu
        weight: Math.max(
          Number(
            data.find((i: any) => i.itemCode === item.itemCode)?.attribute54,
          ) / 1000 || 0,
          MIN_WEIGHT,
        ),
        weightPackage: Math.max(
          Number(
            Number(
              data.find((i: any) => i.itemCode === item.itemCode)?.attribute54 *
              130,
            ) / 100,
          ) / 1000 || 0,
          MIN_WEIGHT_PACKAGE,
        ),
        product_code: item.itemId,
        name: item.itemName,
      }));

      setDataProductUpdateWeight(products);
    }
  };

  const getDataDetailRequestTransfer = async () => {
    open();
    try {
      const res = await repositoryMdm.get<MessageResponse<any>>(
        `/api/v1/TblRequestTransfer/detail?id=${id}`,
      );

      if (res?.success && res) {
        const data = res.data;
        const dataFromBranch = dataBranch.find(
          (i) => String(i.id) === String(data?.fromBranchId),
        );
        const dataToBranch = dataBranch.find(
          (i) => String(i.id) === String(data?.toBranchId),
        );
        setDataSubmit(data);
        getDataEmpManager(res.data?.toBranchId || 0);
        fetchDataCommuneNew(dataFromBranch?.attribute8 || "", "FROM");
        fetchDataCommuneNew(dataToBranch?.attribute8 || "", "TO");

        if (
          data?.tblRequestTransferDetailModels &&
          data?.tblRequestTransferDetailModels?.length > 0
        ) {
          getDataProductWeight(data?.tblRequestTransferDetailModels || []);
        }

        if (res.data?.attribute13 && res.data?.attribute13 === 1) {
          handleGetIdDelivery(res.data?.attribute1 || "", res.data?.code);
        }
      } else {
        close();
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const getDataEmpManager = async (branchId: number) => {
    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblItemManager/get-list?Skip=0&Take=300&IdChiNhanh=${branchId}`,
    );

    if (res && res.success) {
      const data = res.data;

      setDataEmployeeManagerItem(data);
    }
  };

  const fetchDataProvinceNew = async () => {
    const res = await getProvinceSelectNew();

    if (res) {
      setDataProvinceNew(
        res
          ?.filter((item) => item.id && item.name)
          ?.map((item) => ({
            value: String(item.id),
            label: String(item.name),
          })),
      );
    }
  };

  const fetchDataCommuneNew = async (idProvince: string, type: string) => {
    const dataSearch =
      idProvince && idProvince.length > 0 ? `&ProvinceIds=${idProvince}` : "";

    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblDmCommuneMoi/get-select?Active=true${dataSearch}`,
    );

    if (res && res.success) {
      const result = res.data;
      const dataSet = result
        ?.filter((item: any) => item.text && item.value)
        ?.map((item: any) => ({
          value: String(item.value),
          label: String(item.text),
        }));
      if (type === "FROM") {
        setDataCommuneFromBranch(dataSet);
      } else if (type === "TO") {
        setDataCommuneToBranch(dataSet);
      }
    }
  };

  const handleGetIdDelivery = async (
    departmentDelivery: string,
    sourceCode: string,
  ) => {
    const res = await repositoryDelivery.get<MessageResponse<any>>(
      `/api/v1/TblDelivery/get-list${departmentDelivery === "NOI_BO" ? "-gv" : departmentDelivery === "KY_THUAT" ? "-ktkt" : ""}?Skip=0&Take=1&KeySearch=${sourceCode}`,
    );

    if (departmentDelivery && departmentDelivery.length > 0) {
      if (res && res.success) {
        const data = res.data;
        setIdDelivery(data[0]?.id || 0);
      }
    } else {
      setIdDelivery(0);
    }
  };

  const handlePushGHTK = async () => {
    open();
    if (
      dataSubmit?.attribute1 !== "GHTK" &&
      dataSubmit?.attribute1 !== "KHO" &&
      dataSubmit?.attribute13 !== 1
    ) {
      NotificationExtension.Fails(
        "Đơn YCDC này không phù hợp để đăng đơn GHTK hoặc đây không phải đơn đóng gói !",
      );
      close();
      return;
    }

    if (!totalBox || totalBox <= 0) {
      NotificationExtension.Fails("Vui lòng nhập số kiện hàng !");
      close();
      return;
    }

    const productsInvalidWeight = dataProductUpdateWeight.filter(
      (i) => !i.weight || i.weight < MIN_WEIGHT,
    );

    if (productsInvalidWeight.length > 0) {
      NotificationExtension.Fails(
        `Sản phẩm có ID ${productsInvalidWeight
          .map((i) => i.product_code)
          .join(", ")} có khối lượng không hợp lệ (tối thiểu ${MIN_WEIGHT}kg) !`,
      );
      close();
      return;
    }

    const dataFromBranch = dataBranch.find(
      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
    );

    const dataToBranch = dataBranch.find(
      (i) => String(i.id) === String(dataSubmit?.toBranchId),
    );

    const processedProducts = dataProductUpdateWeight.map((item) => {
      const { weightPackage, ...rest } = item;

      return {
        ...rest,
        weight: !isCOD ? item.weight : item.weightPackage,
      };
    });

    try {
      const responseGHTK = await repositoryMdm.post<any>(
        "/api/v1/GHTK/create-order",
        {
          // Token GHTK do server giữ, FE chỉ báo đây có phải đơn thu hộ hay không
          isCod: isCOD,
          // Cập nhật đơn giao hàng nằm luôn trong API này,
          // chạy chung transaction với việc đẩy sang GHTK
          deliveryId: dataSubmit?.attribute13 === 1 ? idDelivery : null,
          note: "Đẩy đơn sang GHTK",
          order: {
            products: processedProducts || [],
            order: {
              id: dataSubmit?.code || null,
              pick_name:
                dataSubmit?.createByName || dataSubmit?.fromBranchName || null,
              pick_address: dataFromBranch?.address,
              pick_province:
                getValueById(
                  String(dataFromBranch.attribute8 || ""),
                  dataProvinceNew,
                  "label",
                ) || null,
              pick_district: null,
              pick_ward:
                getValueById(
                  String(dataFromBranch.attribute9 || ""),
                  dataCommuneFromBranch,
                  "label",
                ) || null,
              pick_tel: dataFromBranch?.attribute11,
              tel: dataSubmit.attribute14,
              name: dataSubmit.attribute15,
              address: dataToBranch?.address,
              province:
                getValueById(
                  String(dataToBranch.attribute8 || ""),
                  dataProvinceNew,
                  "label",
                ) || null,
              district: null,
              ward:
                getValueById(
                  String(dataToBranch.attribute9 || ""),
                  dataCommuneToBranch,
                  "label",
                ) || null,
              hamlet: "Khác",
              is_freeship: "1",
              pick_money: 0,
              note: "",
              value: dataSubmit?.tblRequestTransferDetailModels?.reduce(
                (sum: any, i: any) => {
                  return sum + (i.poPrice * i.primaryQuantity || 0);
                },
                1,
              ),
              pick_option: "cod",
              total_box: totalBox || 1,
            },
          },
        },
      );

      if (responseGHTK?.success === true) {
        NotificationExtension.Success("Tạo đơn GHTK thành công");

        const res = await repositoryMdm.post<MessageResponse<any>>(
          "/api/v1/TblRequestTransfer/update-attribute16",
          {
            id: dataSubmit.id,
            attribute16: String(responseGHTK?.order?.tracking_id),
            attribute17: "GHTK",
            attribute18: responseGHTK?.order?.label,
            attribute19: isCOD ? "DA_DONG_GOI" : null,
            attribute20: "Đẩy đơn GHTK",
            attribute13: dataSubmit?.attribute13
              ? dataSubmit?.attribute13
              : null,
          },
        );

        if (res && res.success) {
          NotificationExtension.Success("Tạo đơn GHTK thành công");
          setTimeout(() => {
            onSuccess();
            modals.closeAll();
          }, 1000);
        }
      } else {
        await repositoryMdm.post<MessageResponse<any>>(
          "/api/v1/TblRequestTransfer/update-attribute16",
          {
            id: dataSubmit.id,
            attribute16: `Fail: ${responseGHTK?.message}`,
            attribute13: dataSubmit?.attribute13
              ? dataSubmit?.attribute13
              : null,
          },
        );

        NotificationExtension.Fails(
          `Lỗi đẩy đơn GHTK: ${responseGHTK?.message}` ||
          "Lỗi đẩy đơn GHTK: Đăng đơn lên GHTK thất bại !",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    getDataDetailRequestTransfer();
    fetchDataProvinceNew();
  }, [id]);

  return (
    <Box pt={10}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Fieldset legend={<Badge>Thông tin chi nhánh nhận</Badge>} mt={10}>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label={"Tên nhân viên chi nhánh nhận"}
                  placeholder="Nhập tên nhân viên chi nhánh nhận"
                  data={dataEmployeeManagerItem
                    .filter((i: any) => i.mnv && i.hoVaTen)
                    .map((i) => ({
                      label: `${i.mnv} - ${i.hoVaTen}`,
                      value: `${i.mnv} - ${i.hoVaTen}`,
                    }))}
                  value={
                    dataSubmit?.attribute15 ? dataSubmit?.attribute15 : null
                  }
                  onChange={(e) =>
                    setDataSubmit((prev: any) => ({
                      ...prev,
                      attribute14: e
                        ? dataEmployeeManagerItem.find(
                          (i) => e === `${i.mnv} - ${i.hoVaTen}`,
                        ).soDienThoaiLienLac
                        : null,
                      attribute15: e ? e : null,
                    }))
                  }
                  withAsterisk
                  error={
                    !dataSubmit?.attribute15 ||
                      dataSubmit?.attribute15.length === 0
                      ? "Tên nhân viên chi nhánh nhận không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label={"Số điện thoại"}
                  placeholder="Nhập số điện thoại"
                  type="number"
                  value={dataSubmit?.attribute14 || null}
                  onChange={(e) => {
                    setDataSubmit((prev: any) => ({
                      ...prev,
                      attribute14: e.target.value,
                    }));
                  }}
                  withAsterisk
                  error={
                    !dataSubmit?.attribute14 ||
                      dataSubmit?.attribute14.length === 0
                      ? "SĐT không được bỏ trống !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Tỉnh/TP (Chi nhánh nhận)"
                  placeholder="Nhập Tỉnh/TP (Chi nhánh nhận)"
                  data={dataProvinceNew}
                  value={
                    String(
                      dataBranch.find(
                        (i) => String(i.id) === String(dataSubmit?.toBranchId),
                      )?.attribute8,
                    ) || null
                  }
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  variant={"filled"}
                  readOnly
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.toBranchId),
                    )?.attribute8 ||
                      String(
                        dataBranch.find(
                          (i) => String(i.id) === String(dataSubmit?.toBranchId),
                        )?.attribute8,
                      ).length === 0
                      ? "Vui lòng cập nhật tỉnh/TP cho chi nhánh nhận ở danh mục chi nhánh !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Phường/xã (Chi nhánh nhận)"
                  placeholder="Nhập Phường/xã chi nhánh nhận"
                  data={dataCommuneToBranch}
                  value={
                    String(
                      dataBranch.find(
                        (i) => String(i.id) === String(dataSubmit?.toBranchId),
                      )?.attribute9,
                    ) || null
                  }
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  variant={"filled"}
                  readOnly
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.toBranchId),
                    )?.attribute9 ||
                      String(
                        dataBranch.find(
                          (i) => String(i.id) === String(dataSubmit?.toBranchId),
                        )?.attribute9,
                      ).length === 0
                      ? "Vui lòng cập nhật phường/xã cho chi nhánh nhận ở danh mục chi nhánh !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ cụ thể ( Không bao gồm Tỉnh/TP, Phường/Xã )"
                  placeholder="Nhập địa chỉ cụ thể chi nhánh nhận"
                  variant={"filled"}
                  readOnly
                  value={
                    dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.toBranchId),
                    )?.attribute10 || ""
                  }
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.toBranchId),
                    )?.attribute10 ||
                      String(
                        dataBranch.find(
                          (i) => String(i.id) === String(dataSubmit?.toBranchId),
                        )?.attribute10,
                      ).length === 0
                      ? "Vui lòng cập nhật địa chỉ cụ thể cho chi nhánh nhận ở danh mục chi nhánh !"
                      : false
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ đầy đủ"
                  placeholder="Địa chỉ đầy đủ"
                  value={
                    dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.toBranchId),
                    )?.address || ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
          </Fieldset>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Fieldset legend={<Badge>Điểm lấy hàng</Badge>} mt={10}>
            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Select
                  label="Chi nhánh xuất"
                  placeholder="Chi nhánh xuất"
                  data={dataBranch
                    .filter((i) => i.id && i.code && i.name)
                    .map((i) => ({
                      label: `${i.code} - ${i.name}`,
                      value: String(i.id),
                    }))}
                  value={String(dataSubmit.fromBranchId || "")}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="SĐT liên hệ lấy hàng của chi nhánh"
                  placeholder="SĐT"
                  type="number"
                  value={
                    dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit.fromBranchId),
                    )?.attribute11 || ""
                  }
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit.fromBranchId),
                    )?.attribute11 ||
                      String(
                        dataBranch.find(
                          (i) => String(i.id) === String(dataSubmit.fromBranchId),
                        )?.attribute11,
                      ).length === 0
                      ? "Vui lòng cập nhật SĐT chi nhánh ở danh mục chi nhánh !"
                      : false
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput
                  label="Số kiện hàng"
                  placeholder="Nhập số kiện hàng"
                  allowNegative={false}
                  min={1}
                  value={totalBox}
                  onChange={(e) => setTotalBox(Number(e))}
                  error={totalBox < 1 && "Số kiện hàng phải lớn hơn 0 !"}
                  withAsterisk
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Tỉnh/TP (Chi nhánh xuất)"
                  placeholder="Tỉnh/TP chi nhánh xuất"
                  data={dataProvinceNew}
                  value={
                    String(
                      dataBranch.find(
                        (i) =>
                          String(i.id) === String(dataSubmit?.fromBranchId),
                      )?.attribute8,
                    ) || null
                  }
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
                    )?.attribute8 ||
                      String(
                        dataBranch.find(
                          (i) =>
                            String(i.id) === String(dataSubmit?.fromBranchId),
                        )?.attribute8,
                      ).length === 0
                      ? "Vui lòng cập nhật tỉnh/TP cho chi nhánh xuất ở danh mục chi nhánh !"
                      : null
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Phường/xã (Chi nhánh xuất)"
                  placeholder="Phường/xã chi nhánh xuất"
                  data={dataCommuneFromBranch}
                  value={
                    String(
                      dataBranch.find(
                        (i) =>
                          String(i.id) === String(dataSubmit?.fromBranchId),
                      )?.attribute9,
                    ) || null
                  }
                  withAsterisk
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
                    )?.attribute9 ||
                      String(
                        dataBranch.find(
                          (i) =>
                            String(i.id) === String(dataSubmit?.fromBranchId),
                        )?.attribute9,
                      ).length === 0
                      ? "Vui lòng cập nhật phường/xã cho chi nhánh xuất ở danh mục chi nhánh !"
                      : false
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ cụ thể ( Không bao gồm Tỉnh/TP, Phường/Xã )"
                  placeholder="Địa chỉ cụ thể"
                  value={
                    dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
                    )?.attribute10 || ""
                  }
                  error={
                    !dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
                    )?.attribute10 ||
                      String(
                        dataBranch.find(
                          (i) =>
                            String(i.id) === String(dataSubmit?.fromBranchId),
                        )?.attribute10,
                      ).length === 0
                      ? "Vui lòng cập nhật địa chỉ cụ thể cho chi nhánh xuất ở danh mục chi nhánh !"
                      : false
                  }
                  variant="filled"
                  readOnly
                  withAsterisk
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <TextInput
                  label="Địa chỉ đầy đủ"
                  placeholder="Địa chỉ đầy đủ"
                  value={
                    dataBranch.find(
                      (i) => String(i.id) === String(dataSubmit?.fromBranchId),
                    )?.address || ""
                  }
                  withAsterisk
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
            </Grid>
          </Fieldset>
        </Grid.Col>
      </Grid>
      <ScrollArea
        h={customHeightTable()}
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
      >
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          mt={10}
        >
          <Table.Thead
            className={cx(classes.header, {
              [classes.scrolled]: scrolled,
            })}
          >
            <Table.Tr>
              <Table.Th>ID sản phẩm</Table.Th>
              <Table.Th>Tên sản phẩm</Table.Th>
              <Table.Th>Số lượng</Table.Th>
              <Table.Th>Khối lượng (kg)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dataProductUpdateWeight && dataProductUpdateWeight.length > 0 ? (
              dataProductUpdateWeight.map((i: Products) => (
                <ProductRow
                  key={i.product_code}
                  item={i}
                  onUpdateWeight={handleUpdateWeight}
                  isCOD={isCOD}
                />
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={4} ta={"center"}>
                  Không tìm thấy sản phẩm !
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Flex justify={"center"} align={"center"} mt={10}>
        <Button
          leftSection={<IconCheck size={18} />}
          color="green"
          onClick={() => {
            handlePushGHTK();
          }}
          disabled={visible}
          loading={visible}
        >
          Hoàn thành
        </Button>
      </Flex>
    </Box>
  );
};

export default ConfirmInfoGHTKRequestTransfer;
